import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  CandlestickColors,
  ChartTypeKey,
  IntervalKey,
  LineConfig,
  PeriodKey,
  TAItem,
  ViewState,
} from './types';
import {
  DEFAULT_ACTIVE_TA,
  DEFAULT_BAR_CONFIG,
  DEFAULT_CANDLESTICK_COLORS,
  DEFAULT_LINE_CONFIG,
  DEFAULT_VIEW,
} from './constants';

const STORAGE_KEY = 'advance-chart-widget-settings';

interface PersistedSettings {
  interval: IntervalKey;
  period: PeriodKey;
  chartType: ChartTypeKey;
  candlestickColors: CandlestickColors;
  lineConfig: LineConfig;
  barConfig: LineConfig;
  activeTA: TAItem[];
  view: ViewState;
}

interface ChartSettingsContextValue extends PersistedSettings {
  setChartInterval: (v: IntervalKey) => void;
  setChartPeriod: (v: PeriodKey) => void;
  setChartType: (v: ChartTypeKey) => void;
  setCandlestickColors: (v: CandlestickColors) => void;
  setLineConfig: (v: LineConfig) => void;
  setBarConfig: (v: LineConfig) => void;
  setActiveTA: React.Dispatch<React.SetStateAction<TAItem[]>>;
  setView: React.Dispatch<React.SetStateAction<ViewState>>;
  resetToDefault: () => void;
}

const DEFAULT_SETTINGS: PersistedSettings = {
  interval: 'd',
  period: '6M',
  chartType: 'candlestick',
  candlestickColors: DEFAULT_CANDLESTICK_COLORS,
  lineConfig: DEFAULT_LINE_CONFIG,
  barConfig: DEFAULT_BAR_CONFIG,
  activeTA: DEFAULT_ACTIVE_TA,
  view: DEFAULT_VIEW,
};

function loadFromStorage(): PersistedSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

const ChartSettingsContext = createContext<ChartSettingsContextValue | null>(null);

export const ChartSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<PersistedSettings>(loadFromStorage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const setChartInterval = useCallback((interval: IntervalKey) => {
    setSettings((prev) => ({ ...prev, interval }));
  }, []);

  const setChartPeriod = useCallback((period: PeriodKey) => {
    setSettings((prev) => ({ ...prev, period }));
  }, []);

  const setChartType = useCallback((chartType: ChartTypeKey) => {
    setSettings((prev) => ({ ...prev, chartType }));
  }, []);

  const setCandlestickColors = useCallback((candlestickColors: CandlestickColors) => {
    setSettings((prev) => ({ ...prev, candlestickColors }));
  }, []);

  const setLineConfig = useCallback((lineConfig: LineConfig) => {
    setSettings((prev) => ({ ...prev, lineConfig }));
  }, []);

  const setBarConfig = useCallback((barConfig: LineConfig) => {
    setSettings((prev) => ({ ...prev, barConfig }));
  }, []);

  const setActiveTA = useCallback<React.Dispatch<React.SetStateAction<TAItem[]>>>((action) => {
    setSettings((prev) => ({
      ...prev,
      activeTA: typeof action === 'function' ? action(prev.activeTA) : action,
    }));
  }, []);

  const setView = useCallback<React.Dispatch<React.SetStateAction<ViewState>>>((action) => {
    setSettings((prev) => ({
      ...prev,
      view: typeof action === 'function' ? action(prev.view) : action,
    }));
  }, []);

  const resetToDefault = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return (
    <ChartSettingsContext.Provider
      value={{
        ...settings,
        setChartInterval,
        setChartPeriod,
        setChartType,
        setCandlestickColors,
        setLineConfig,
        setBarConfig,
        setActiveTA,
        setView,
        resetToDefault,
      }}
    >
      {children}
    </ChartSettingsContext.Provider>
  );
};

export const useChartSettings = (): ChartSettingsContextValue => {
  const ctx = useContext(ChartSettingsContext);
  if (!ctx) throw new Error('useChartSettings must be used within ChartSettingsProvider');
  return ctx;
};
